/*
  REST Text2Speech component, gets a sentance
  and plays the answer on the browser
 */

import { observer } from "mobx-react";
import React, {useRef, useEffect} from "react";
import { store } from "stores/Store";
import { InterruptControllerRef } from "components/InterruptController/InterruptController";

  const getApiKey = async () => {
      const myDGKey = process.env.REACT_APP_DG_KEY || '';
      if (!myDGKey) {
          console.error('Deepgram API key not found');
      }
      return myDGKey;     // Got rid of hard-coding of the API Key.  TODO: need to protect this key by extracting from backend.
  };

  // ========== PHASE 1: TEXT PREPROCESSING ==========

  /**
   * Preprocesses text for TTS by removing/replacing problematic characters
   * - Removes emojis (spoken as emoji names)
   * - Replaces special chars with spoken equivalents ($→dollar, %→percent)
   * - Cleans whitespace and markdown formatting
   */
  const preprocessTextForTTS = (text: string): string => {
    // Remove emojis using a more robust approach
    // This handles emoji ranges including: basic emojis, skin tones, flags, symbols
    text = text.replace(/[\u{1F600}-\u{1F64F}]/gu, '');  // Emoticons
    text = text.replace(/[\u{1F300}-\u{1F5FF}]/gu, '');  // Misc Symbols and Pictographs
    text = text.replace(/[\u{1F680}-\u{1F6FF}]/gu, '');  // Transport and Map
    text = text.replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '');  // Flags
    text = text.replace(/[\u{2600}-\u{26FF}]/gu, '');    // Misc symbols
    text = text.replace(/[\u{2700}-\u{27BF}]/gu, '');    // Dingbats
    text = text.replace(/[\u{FE00}-\u{FE0F}]/gu, '');    // Variation Selectors
    text = text.replace(/[\u{1F900}-\u{1F9FF}]/gu, '');  // Supplemental Symbols and Pictographs
    text = text.replace(/[\u{1FA70}-\u{1FAFF}]/gu, '');  // Symbols and Pictographs Extended-A

    // Replace special characters with spoken equivalents using split/join (not regex)
    const replacements: Record<string, string> = {
      '$': ' dollar ',
      '%': ' percent ',
      '&': ' and ',
      '@': ' at ',
      '#': ' number ',
      '×': ' times ',
      '÷': ' divided by ',
      '±': ' plus or minus ',
      '≈': ' approximately ',
      '≠': ' not equal to ',
      '≤': ' less than or equal to ',
      '≥': ' greater than or equal to ',
    };

    // Use split/join instead of regex for simple character replacement
    Object.entries(replacements).forEach(([char, replacement]) => {
      text = text.split(char).join(replacement);
    });

    // Clean whitespace characters
    text = text.replace(/\n/g, '. ');  // Newlines become sentence breaks
    text = text.replace(/\t/g, ' ');   // Tabs become spaces
    text = text.replace(/\r/g, '');    // Remove carriage returns

    // Remove markdown formatting (preserve content) - do this AFTER special char replacement
    text = text.replace(/\*\*(.*?)\*\*/g, '$1');  // Bold
    text = text.replace(/\*(.*?)\*/g, '$1');      // Italic
    text = text.replace(/`([^`]+)`/g, '$1');      // Inline code
    text = text.replace(/~~(.*?)~~/g, '$1');      // Strikethrough
    text = text.split('_').join('');  // Remove underscores
    text = text.split('~').join('');  // Remove tildes

    // Clean up multiple spaces and trim
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  };

  // ========== PHASE 2: TEXT CHUNKING HELPERS ==========

  /**
   * Extracts the first sentence from text for quick initial playback
   * Falls back to first ~300 chars at word boundary if no sentence found
   */
  const getFirstSentence = (text: string): string => {
    // Match sentence ending: period, exclamation, or question mark
    const sentenceEndPattern = /[.!?](?=\s|$)/;
    const match = sentenceEndPattern.exec(text);

    if (match && match.index !== undefined) {
      const firstSentence = text.substring(0, match.index + 1).trim();
      // Ensure first sentence is at least 20 chars (avoid single-word sentences)
      if (firstSentence.length >= 20) {
        return firstSentence;
      }
    }

    // Fallback: if no good sentence boundary found, break at word boundary around 300 chars
    if (text.length > 300) {
      const cutoff = text.substring(0, 300).lastIndexOf(' ');
      return text.substring(0, cutoff > 0 ? cutoff : 300).trim();
    }

    // Text is short enough, return as-is
    return text;
  };

  /**
   * Truncates text at sentence boundary near maxChars limit
   * Preserves natural speech flow by never splitting mid-sentence
   */
  const truncateAtSentence = (text: string, maxChars: number): string => {
    if (text.length <= maxChars) {
      return text;
    }

    // Search for sentence boundary in the last 200 chars before maxChars
    const searchStart = Math.max(0, maxChars - 200);
    const searchText = text.substring(searchStart, maxChars);

    // Find last sentence boundary in search window
    const sentenceEndPattern = /[.!?](?=\s|$)/g;
    let lastMatch = null;
    let match;

    while ((match = sentenceEndPattern.exec(searchText)) !== null) {
      lastMatch = match;
    }

    if (lastMatch && lastMatch.index !== undefined) {
      const truncateIndex = searchStart + lastMatch.index + 1;
      return text.substring(0, truncateIndex).trim();
    }

    // Fallback to word boundary if no sentence found
    const cutoff = text.substring(0, maxChars).lastIndexOf(' ');
    const truncated = text.substring(0, cutoff > 0 ? cutoff : maxChars).trim();

    console.warn(`[DeepgramT2S] Truncated at word boundary (no sentence found): ${text.length} → ${truncated.length} chars`);
    return truncated;
  };

  interface IText2Speech {
      audioCtx: AudioContext;
      isVoicePlaying: boolean;
      setIsVoicePlaying: (isVoicePlaying: boolean) => void;
      interruptControllerRef: React.MutableRefObject<InterruptControllerRef | null>;
      onContinuationPrompt?: (continueCallback: () => void, remainingChars: number) => void;
  }

  const DeepgramT2S: React.FC<IText2Speech> = observer(
    ({ audioCtx, isVoicePlaying, setIsVoicePlaying, interruptControllerRef, onContinuationPrompt }) => {
      // holds the current playing buffer source so we can stop it on interrupt
      const sourceRef = useRef<AudioBufferSourceNode | null>(null);
      const isProcessingRef = useRef<boolean>(false);

      // Track pending second chunk to play after first completes
      const pendingSecondChunkRef = useRef<string | null>(null);

      // ========== PHASE 3: CONTINUATION TRACKING ==========

      // Store full preprocessed text for continuation feature
      const fullPreprocessedTextRef = useRef<string>('');
      // Track current character position in the full text
      const currentCharPositionRef = useRef<number>(0);

      // Track last answer index to detect when streaming starts
      const lastAnswerIndexRef = useRef<number>(-1);

      // ========== PHASE 2: FETCH AND PLAY AUDIO CHUNK ==========

      /**
       * Fetches audio from Deepgram TTS API and plays it
       * @param text - Preprocessed text to convert to speech
       * @param isSecondChunk - Whether this is the second chunk (for logging)
       */
      const fetchAndPlayChunk = async (text: string, isSecondChunk: boolean = false) => {
        try {
          const apiKey = await getApiKey();
          if (!apiKey) {
            throw new Error("No Deepgram API key found");
          }

          const chunkLabel = isSecondChunk ? "Chunk 2" : "Chunk 1";
          console.log(`[DeepgramT2S] ${chunkLabel}: Fetching audio for ${text.length} chars`);

          const response = await fetch(
            "https://api.deepgram.com/v1/speak?model=aura-athena-en",
            {
              method: "POST",
              headers: {
                Authorization: `Token ${apiKey}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ text }),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`TTS API failed (${response.status}): ${errorText}`);
          }

          // Check if we should stop before proceeding
          if (interruptControllerRef.current?.isForceStopped()) {
            throw new Error("Interrupted before audio decode");
          }

          // Get raw bytes and decode to AudioBuffer
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

          console.log(`[DeepgramT2S] ${chunkLabel}: Audio decoded, duration ${audioBuffer.duration.toFixed(2)}s`);

          // Check again if we should stop
          if (interruptControllerRef.current?.isForceStopped()) {
            throw new Error("Interrupted after audio decode");
          }

          // Stop any prior playback
          if (sourceRef.current) {
            sourceRef.current.stop();
            sourceRef.current = null;
          }

          // Create & play new buffer source
          const src = audioCtx.createBufferSource();
          src.buffer = audioBuffer;
          src.connect(audioCtx.destination);

          // Handle playback completion
          src.onended = () => {
            console.log(`[DeepgramT2S] ${chunkLabel}: Playback completed`);
            sourceRef.current = null;

            // If this was first chunk and we have a second chunk pending, play it
            if (!isSecondChunk && pendingSecondChunkRef.current) {
              const secondChunk = pendingSecondChunkRef.current;
              pendingSecondChunkRef.current = null;
              console.log(`[DeepgramT2S] Starting second chunk: ${secondChunk.length} chars`);
              fetchAndPlayChunk(secondChunk, true).catch(err => {
                console.error('[DeepgramT2S] Second chunk failed:', err);
                isProcessingRef.current = false;
                setIsVoicePlaying(false);
              });
            } else {
              // Chunk 2 or final chunk completed - check for continuation
              const remainingChars = fullPreprocessedTextRef.current.length - currentCharPositionRef.current;

              if (remainingChars > 20 && onContinuationPrompt) {
                // More text exists - prompt user for continuation
                console.log(`[DeepgramT2S] Continuation available: ${remainingChars} chars remaining`);

                const continueCallback = () => {
                  console.log('[DeepgramT2S] User chose to continue');
                  const nextChunkText = fullPreprocessedTextRef.current.substring(currentCharPositionRef.current);
                  const nextChunk = truncateAtSentence(nextChunkText, 1900);
                  currentCharPositionRef.current += nextChunk.length;

                  console.log(`[DeepgramT2S] Continuation chunk: ${nextChunk.length} chars (position now ${currentCharPositionRef.current}/${fullPreprocessedTextRef.current.length})`);
                  fetchAndPlayChunk(nextChunk, true).catch(err => {
                    console.error('[DeepgramT2S] Continuation chunk failed:', err);
                    isProcessingRef.current = false;
                    setIsVoicePlaying(false);
                  });
                };

                // Trigger parent to show continuation prompt
                onContinuationPrompt(continueCallback, remainingChars);
              } else {
                // All done - no more text or no continuation callback
                console.log('[DeepgramT2S] All playback completed');
                isProcessingRef.current = false;
                setIsVoicePlaying(false);
              }
            }
          };

          sourceRef.current = src;
          src.start(0);
          setIsVoicePlaying(true);

        } catch (err: any) {
          console.error(`[DeepgramT2S] Error in ${isSecondChunk ? 'second' : 'first'} chunk:`, {
            error: err.message,
            textLength: text.length,
            interrupted: err.message?.includes('Interrupted')
          });

          if (!isSecondChunk) {
            // First chunk failed, abort completely
            isProcessingRef.current = false;
            setIsVoicePlaying(false);
            pendingSecondChunkRef.current = null;
          } else {
            // Second chunk failed, but first already played
            isProcessingRef.current = false;
            setIsVoicePlaying(false);
          }
        }
      };

      // ========== MAIN EFFECT: TRIGGER TTS WHEN ANSWER COMPLETES ==========

      // Access observables in component body so observer() tracks them
      const currentIndex = store.answers.length - 1;
      const currentMessage = currentIndex >= 0 ? store.answers[currentIndex]?.message || '' : '';

      useEffect(() => {
        if (!currentMessage) return;

        // Skip if already processing
        if (isProcessingRef.current) return;

        // Check if this is a NEW answer (not already processed)
        const isNewAnswer = currentIndex !== lastAnswerIndexRef.current;
        if (!isNewAnswer) return;

        // New answer detected
        lastAnswerIndexRef.current = currentIndex;

        (async () => {
          try {
            isProcessingRef.current = true;
            pendingSecondChunkRef.current = null;

            // Phase 1: Preprocess text to remove emojis, special chars, markdown
            const preprocessed = preprocessTextForTTS(currentMessage);

            if (!preprocessed.trim()) {
              isProcessingRef.current = false;
              return;
            }

            // Phase 3: Store full text for continuation feature
            fullPreprocessedTextRef.current = preprocessed;
            currentCharPositionRef.current = 0;

            // Phase 2: Split into chunks for streaming playback
            const firstSentence = getFirstSentence(preprocessed);
            const remainingText = preprocessed.substring(firstSentence.length).trim();

            // Track how much will be played in initial chunks
            let totalInitialChars = firstSentence.length;

            // If remaining text exists and is substantial, prepare as second chunk
            if (remainingText.length > 20) {
              const secondChunk = truncateAtSentence(remainingText, 1900);
              pendingSecondChunkRef.current = secondChunk;
              totalInitialChars += secondChunk.length;
            }

            // Set position to end of what will be played in chunks 1+2
            currentCharPositionRef.current = totalInitialChars;

            // Start playback with first chunk
            await fetchAndPlayChunk(firstSentence, false);

          } catch (err: any) {
            console.error('[DeepgramT2S] Error in TTS pipeline:', err);
            isProcessingRef.current = false;
            setIsVoicePlaying(false);
            pendingSecondChunkRef.current = null;
          }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentMessage, currentIndex, audioCtx, interruptControllerRef]);

      // Handle interrupt signal
      useEffect(() => {
        if (!isVoicePlaying && sourceRef.current) {
          console.log('[DeepgramT2S] Interrupt signal received, stopping playback');
          sourceRef.current.stop();
          sourceRef.current = null;
          isProcessingRef.current = false;
          pendingSecondChunkRef.current = null;  // Cancel pending second chunk
          // Reset continuation tracking (user interrupted, don't prompt for continuation)
          fullPreprocessedTextRef.current = '';
          currentCharPositionRef.current = 0;
        }
      }, [isVoicePlaying]);

      // No <audio> tag needed—playback is via the AudioContext
      return null;
    }
  );

  export default DeepgramT2S;
